import { ReactNode, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface ProtectedRouteProps {
  children: ReactNode;
  requireAdmin?: boolean;
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    console.log('🔐 ProtectedRoute Check:', { 
      hasUser: !!user, 
      userEmail: user?.email,
      loading,
      hasRedirected: hasRedirectedRef.current
    });

    // Wait for auth to finish loading
    if (loading) {
      console.log('⏳ Still loading auth state...');
      return;
    }

    // If no user after loading, redirect to login (only once)
    if (!user && !hasRedirectedRef.current) {
      console.log('❌ No user found, redirecting to /login');
      hasRedirectedRef.current = true;
      navigate('/login', { replace: true });
    } else if (user) {
      console.log('✅ User authenticated:', user.email);
      hasRedirectedRef.current = false;
    }
  }, [user, loading, navigate]);

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render children if no user
  if (!user) {
    return null;
  }

  return <>{children}</>;
}
